/* ══════════════════════════════════════════════════════════════
 *  Data Quality — Admin Section
 *  Sub-tabs: Duplicates, Missing Data, Merge History
 * ══════════════════════════════════════════════════════════════ */
import { useState, useEffect, useRef } from 'react';
import css from '../AdminPage.module.css';
import {
  useRunDuplicateScan,
  useScanStatus,
  useMissingDataStats,
  useMissingDataContacts,
  useMergeHistory,
  useClusterSummary,
  useClusters,
  useClusterDetails,
  useDismissCluster,
  useMergeCluster,
  useRunCaseDuplicateScan,
  useCaseScanStatus,
  useCaseDuplicateGroupSummary,
  useCaseDuplicateGroups,
  useCaseDuplicateGroupDetails,
  useDismissCaseDuplicateGroup,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import type {
  MissingDataContact,
  ContactMergeLog,
  DuplicateCluster,
  ClusterMember,
  CaseDuplicateGroup,
  CaseDuplicateGroupCase,
} from '../../../types/admin';

type SubTab = 'duplicates' | 'case-duplicates' | 'missing-data' | 'merge-history';
type MissingField =
  | 'email'
  | 'phone'
  | 'dob'
  | 'first_name'
  | 'last_name'
  | 'address'
  | 'postcode'
  | 'contact_method';

const MATCH_LABELS: Record<string, string> = {
  email_exact: 'Email Match',
  phone_exact: 'Phone Match',
  name_dob: 'Name + DOB',
  name_postcode: 'Name + Postcode',
  name_fuzzy: 'Fuzzy Name',
};

const MISSING_FIELDS: { key: MissingField; label: string; statKey: string }[] = [
  { key: 'email', label: 'Email', statKey: 'missing_email' },
  { key: 'phone', label: 'Phone', statKey: 'missing_phone' },
  { key: 'dob', label: 'Date of Birth', statKey: 'missing_dob' },
  { key: 'first_name', label: 'First Name', statKey: 'missing_first_name' },
  { key: 'last_name', label: 'Last Name', statKey: 'missing_last_name' },
  { key: 'address', label: 'Address', statKey: 'missing_address' },
  { key: 'postcode', label: 'Postcode', statKey: 'missing_postcode' },
  { key: 'contact_method', label: 'No Email or Phone', statKey: 'missing_any_contact_method' },
];

export default function DataQuality() {
  const [subTab, setSubTab] = useState<SubTab>('duplicates');

  return (
    <div className={css.section}>
      <h2 className={css.sectionTitle}>Data Quality</h2>

      <div className={css.tabs}>
        {(
          [
            ['duplicates', 'Contact Duplicates'],
            ['case-duplicates', 'Case Duplicates'],
            ['missing-data', 'Missing Data'],
            ['merge-history', 'Merge History'],
          ] as [SubTab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            className={`${css.tab} ${subTab === key ? css.tabActive : ''}`}
            onClick={() => setSubTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {subTab === 'duplicates' && <DuplicatesPanel />}
      {subTab === 'case-duplicates' && <CaseDuplicatesPanel />}
      {subTab === 'missing-data' && <MissingDataPanel />}
      {subTab === 'merge-history' && <MergeHistoryPanel />}
    </div>
  );
}

/* ═══════════════════════════════════════════
 *  Duplicates Panel (Cluster-based)
 * ═══════════════════════════════════════════ */
function DuplicatesPanel() {
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<'size' | 'confidence'>('size');
  const pageSize = 20;

  const { showToast } = useToast();
  const { data: clusterSummary, refetch: refetchSummary } = useClusterSummary();
  const {
    data: clustersData,
    isLoading,
    refetch: refetchClusters,
  } = useClusters('open', pageSize, page * pageSize, sortBy);
  const scanMutation = useRunDuplicateScan();

  // Async scan polling
  const [scanning, setScanning] = useState(false);
  const { data: scanStatus } = useScanStatus(scanning);
  const prevStatusRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = scanStatus?.status;
    prevStatusRef.current = curr;

    if (!curr || curr === prev) return;

    if (curr === 'complete' && prev === 'running') {
      setScanning(false);
      const r = scanStatus?.result;
      showToast(
        `Scan complete: ${r?.inserted ?? '?'} issues found, ${r?.clusters?.total_clusters ?? '?'} clusters built.`,
        'success',
        6000,
      );
      refetchSummary();
      refetchClusters();
    } else if (curr === 'error' && prev === 'running') {
      setScanning(false);
      showToast(`Scan failed: ${scanStatus?.error ?? 'unknown error'}`, 'error', 6000);
    }
  }, [scanStatus?.status]);

  const [expandedCluster, setExpandedCluster] = useState<number | null>(null);
  const [mergeClusterId, setMergeClusterId] = useState<number | null>(null);
  const [dismissTarget, setDismissTarget] = useState<number | null>(null);
  const [dismissNote, setDismissNote] = useState('');

  const dismissClusterMutation = useDismissCluster();
  const mergeClusterMutation = useMergeCluster();

  const handleScan = () => {
    scanMutation.mutate(undefined, {
      onSuccess: () => {
        setScanning(true);
        showToast('Scan started — this may take a minute...', 'info', 5000);
      },
      onError: (err) => {
        showToast(`Could not start scan: ${(err as Error).message}`, 'error');
      },
    });
  };

  const handleDismissCluster = (id: number) => {
    dismissClusterMutation.mutate(
      { id, note: dismissNote },
      {
        onSuccess: () => {
          setDismissTarget(null);
          setDismissNote('');
          showToast('Cluster dismissed.', 'success');
        },
      },
    );
  };

  const isScanning = scanMutation.isPending || scanning;
  const totalPages = clustersData ? Math.ceil(clustersData.total / pageSize) : 0;

  return (
    <div style={{ marginTop: '1rem' }}>
      {/* Scan Button + Progress Bar */}
      <div
        style={{
          display: 'flex',
          gap: '1.5rem',
          alignItems: 'center',
          marginBottom: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <button onClick={handleScan} disabled={isScanning} className={css.btnPrimary}>
          {isScanning ? 'Scanning...' : 'Scan for Duplicates'}
        </button>
        {isScanning && (
          <div style={{ flex: 1, minWidth: '200px', maxWidth: '300px' }}>
            <div
              style={{
                height: '6px',
                borderRadius: '3px',
                background: 'var(--bg-secondary)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  borderRadius: '3px',
                  background: 'var(--accent-primary)',
                  animation: 'indeterminate 1.5s infinite ease-in-out',
                  width: '40%',
                }}
              />
            </div>
            <style>{`@keyframes indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }`}</style>
          </div>
        )}
      </div>

      {/* Cluster Summary */}
      {clusterSummary && (
        <div
          style={{
            display: 'flex',
            gap: '1.5rem',
            alignItems: 'center',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            fontSize: '0.85rem',
          }}
        >
          <span>
            Open clusters: <strong>{clusterSummary.open}</strong>
          </span>
          <span>
            Dismissed: <strong>{clusterSummary.dismissed}</strong>
          </span>
          <span>
            Resolved: <strong>{clusterSummary.resolved}</strong>
          </span>
          {clusterSummary.sizeDistribution && (
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
              {clusterSummary.sizeDistribution.pairs != null && (
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: 'var(--bg-secondary)',
                    fontSize: '0.78rem',
                  }}
                >
                  Pairs: {clusterSummary.sizeDistribution.pairs}
                </span>
              )}
              {clusterSummary.sizeDistribution.small_groups != null && (
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: 'var(--warning-50)',
                    color: 'var(--warning-600)',
                    fontSize: '0.78rem',
                  }}
                >
                  3-5: {clusterSummary.sizeDistribution.small_groups}
                </span>
              )}
              {clusterSummary.sizeDistribution.medium_groups != null && (
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: 'var(--warning-50)',
                    color: 'var(--warning-500)',
                    fontSize: '0.78rem',
                  }}
                >
                  6-20: {clusterSummary.sizeDistribution.medium_groups}
                </span>
              )}
              {clusterSummary.sizeDistribution.large_groups != null && (
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: 'var(--error-50)',
                    color: 'var(--error-600)',
                    fontSize: '0.78rem',
                  }}
                >
                  20+: {clusterSummary.sizeDistribution.large_groups}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sort */}
      <div style={{ marginBottom: '0.75rem' }}>
        <select
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value as 'size' | 'confidence');
            setPage(0);
          }}
          className={css.formSelect}
          style={{ width: 'auto' }}
        >
          <option value="size">Sort by cluster size</option>
          <option value="confidence">Sort by confidence</option>
        </select>
      </div>

      {/* Cluster List */}
      {isLoading ? (
        <p>Loading...</p>
      ) : !clustersData?.clusters.length ? (
        <p style={{ opacity: 0.6 }}>No open duplicate clusters. Run a scan to check.</p>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {clustersData.clusters.map((cluster: DuplicateCluster) => (
              <ClusterRow
                key={cluster.id}
                cluster={cluster}
                isExpanded={expandedCluster === cluster.id}
                onToggle={() =>
                  setExpandedCluster(expandedCluster === cluster.id ? null : cluster.id)
                }
                onMerge={() => setMergeClusterId(cluster.id)}
                onDismiss={() => setDismissTarget(cluster.id)}
                dismissTarget={dismissTarget}
                dismissNote={dismissNote}
                setDismissNote={setDismissNote}
                onConfirmDismiss={() => handleDismissCluster(cluster.id)}
                onCancelDismiss={() => setDismissTarget(null)}
                isDismissing={dismissClusterMutation.isPending}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'center',
                marginTop: '0.75rem',
              }}
            >
              <button
                className={css.btnSmall}
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </button>
              <span style={{ padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                className={css.btnSmall}
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Group Merge Modal */}
      {mergeClusterId !== null && (
        <ClusterMergeModal
          clusterId={mergeClusterId}
          onClose={() => setMergeClusterId(null)}
          onMerge={(masterId, contactIds) => {
            const count = contactIds.length - 1;
            if (
              !window.confirm(
                `Merge ${count} contact${count > 1 ? 's' : ''} into ${masterId}? This cannot be undone.`,
              )
            )
              return;
            mergeClusterMutation.mutate(
              { clusterId: mergeClusterId, masterContactId: masterId, contactIds },
              {
                onSuccess: (result) => {
                  setMergeClusterId(null);
                  const partialNote = result.partial
                    ? ' (partial — remaining contacts still in cluster)'
                    : '';
                  showToast(
                    `Merged ${result.merged} contacts into ${masterId}${result.failed ? ` (${result.failed} failed)` : ''}${partialNote}.`,
                    result.failed ? 'error' : 'success',
                  );
                },
                onError: (err) => showToast(`Merge failed: ${(err as Error).message}`, 'error'),
              },
            );
          }}
          isPending={mergeClusterMutation.isPending}
        />
      )}
    </div>
  );
}

/* ── Cluster Row (expandable) ── */
function ClusterRow({
  cluster,
  isExpanded,
  onToggle,
  onMerge,
  onDismiss,
  dismissTarget,
  dismissNote,
  setDismissNote,
  onConfirmDismiss,
  onCancelDismiss,
  isDismissing,
}: {
  cluster: DuplicateCluster;
  isExpanded: boolean;
  onToggle: () => void;
  onMerge: () => void;
  onDismiss: () => void;
  dismissTarget: number | null;
  dismissNote: string;
  setDismissNote: (v: string) => void;
  onConfirmDismiss: () => void;
  onCancelDismiss: () => void;
  isDismissing: boolean;
}) {
  const { data: detail } = useClusterDetails(isExpanded ? cluster.id : null);

  const sizeColor =
    cluster.member_count > 20
      ? 'var(--error-50)'
      : cluster.member_count > 5
        ? 'var(--warning-50)'
        : cluster.member_count > 2
          ? 'var(--info-50)'
          : 'var(--surface-secondary)';

  return (
    <div
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        overflow: 'hidden',
        background: isExpanded ? 'var(--bg-secondary)' : 'transparent',
      }}
    >
      {/* Header row */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          padding: '0.6rem 0.8rem',
          cursor: 'pointer',
          fontSize: '0.85rem',
        }}
      >
        <span style={{ fontFamily: 'monospace', width: '1.2rem', textAlign: 'center' }}>
          {isExpanded ? '▾' : '▸'}
        </span>
        <span
          style={{
            padding: '2px 10px',
            borderRadius: '12px',
            fontWeight: 700,
            fontSize: '0.8rem',
            background: sizeColor,
            color: 'var(--text-primary)',
            minWidth: '2rem',
            textAlign: 'center',
          }}
        >
          {cluster.member_count}
        </span>
        <span style={{ fontWeight: 600 }}>{cluster.member_count} contacts</span>
        <span style={{ fontSize: '0.78rem', opacity: 0.6 }}>
          Best match: {(cluster.best_confidence * 100).toFixed(0)}%
        </span>
        <div style={{ display: 'flex', gap: '0.3rem', marginLeft: '0.5rem' }}>
          {cluster.match_types.map((mt: string) => (
            <span
              key={mt}
              style={{
                padding: '1px 6px',
                borderRadius: '10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
              }}
            >
              {MATCH_LABELS[mt] || mt}
            </span>
          ))}
        </div>
        <div
          style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className={css.btnSmall} style={{ fontSize: '0.75rem' }} onClick={onMerge}>
            Merge
          </button>
          {dismissTarget === cluster.id ? (
            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
              <input
                placeholder="Note (optional)"
                value={dismissNote}
                onChange={(e) => setDismissNote(e.target.value)}
                style={{
                  width: '120px',
                  padding: '2px 6px',
                  fontSize: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                }}
              />
              <button
                className={css.btnSmall}
                style={{ fontSize: '0.7rem' }}
                onClick={onConfirmDismiss}
                disabled={isDismissing}
              >
                OK
              </button>
              <button
                className={css.btnSmall}
                style={{ fontSize: '0.7rem' }}
                onClick={onCancelDismiss}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className={css.btnSmall}
              style={{ fontSize: '0.75rem', opacity: 0.7 }}
              onClick={onDismiss}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {isExpanded && detail && (
        <div style={{ padding: '0 0.8rem 0.8rem', borderTop: '1px solid var(--border-color)' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.82rem',
              marginTop: '0.5rem',
            }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                <th style={{ padding: '0.3rem 0.5rem' }}>Contact ID</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Name</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Email</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Phone</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Postcode</th>
              </tr>
            </thead>
            <tbody>
              {detail.members.map((m: ClusterMember) => (
                <tr
                  key={m.ContactID}
                  onClick={() => window.open(`/contacts/${m.ContactID}`, '_blank')}
                  style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                  title="Open contact"
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.04))')
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  <td
                    style={{
                      padding: '0.3rem 0.5rem',
                      fontFamily: 'monospace',
                      fontSize: '0.78rem',
                    }}
                  >
                    {m.ContactID}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    {m['First Name']} {m['Last Name']}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                    {m.Email || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                    {m['Phone (main)'] || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                    {m.Postcode || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {detail.issues.length > 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', opacity: 0.6 }}>
              {detail.issues.length} pair connection{detail.issues.length !== 1 ? 's' : ''} in this
              cluster
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Cluster Merge Modal ── */
function ClusterMergeModal({
  clusterId,
  onClose,
  onMerge,
  isPending,
}: {
  clusterId: number;
  onClose: () => void;
  onMerge: (masterContactId: string, contactIds: string[]) => void;
  isPending: boolean;
}) {
  const { data: detail, isLoading } = useClusterDetails(clusterId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [masterId, setMasterId] = useState<string | null>(null);

  // Auto-select all members on load
  useEffect(() => {
    if (detail?.members.length && selectedIds.size === 0) {
      const all = new Set(detail.members.map((m: ClusterMember) => m.ContactID));
      setSelectedIds(all);
      setMasterId(detail.members[0].ContactID);
    }
  }, [detail, selectedIds.size]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (masterId === id) setMasterId([...next][0] ?? null);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const mergeCount = masterId ? selectedIds.size - 1 : 0;
  const isPartial = detail ? selectedIds.size < detail.members.length : false;

  return (
    <div className={css.modalBackdrop} onClick={onClose}>
      <div className={css.modalXl} onClick={(e) => e.stopPropagation()}>
        <div className={css.modalHeader}>
          <span className={css.modalTitle}>
            Merge Cluster ({detail?.members.length ?? '...'} contacts)
          </span>
          <button className={css.modalClose} onClick={onClose} disabled={isPending}>
            &times;
          </button>
        </div>

        <div className={css.modalBody}>
          <p style={{ fontSize: '0.85rem', opacity: 0.7, margin: '0 0 0.75rem' }}>
            Select which contacts to merge and choose a master to keep. Unselected contacts remain
            in the cluster for later review.
          </p>

          {isLoading ? (
            <p>Loading members...</p>
          ) : detail?.members.length ? (
            <>
              <div
                style={{
                  maxHeight: '50vh',
                  overflow: 'auto',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr
                      style={{
                        position: 'sticky',
                        top: 0,
                        background: 'var(--surface-primary)',
                        zIndex: 1,
                        textAlign: 'left',
                        boxShadow: '0 1px 0 var(--border-color)',
                      }}
                    >
                      <th style={{ padding: '0.5rem 0.4rem', width: '2rem' }}>
                        <input
                          type="checkbox"
                          checked={
                            detail.members.length > 0 && selectedIds.size === detail.members.length
                          }
                          ref={(el) => {
                            if (el)
                              el.indeterminate =
                                selectedIds.size > 0 && selectedIds.size < detail.members.length;
                          }}
                          onChange={() => {
                            if (selectedIds.size === detail.members.length) {
                              const firstId = detail.members[0].ContactID;
                              setSelectedIds(new Set([firstId]));
                              setMasterId(firstId);
                            } else {
                              setSelectedIds(
                                new Set(detail.members.map((m: ClusterMember) => m.ContactID)),
                              );
                            }
                          }}
                        />
                      </th>
                      <th style={{ padding: '0.5rem 0.4rem', width: '2rem' }} title="Master">
                        ★
                      </th>
                      <th style={{ padding: '0.5rem 0.4rem' }}>Name</th>
                      <th style={{ padding: '0.5rem 0.4rem' }}>ID</th>
                      <th style={{ padding: '0.5rem 0.4rem' }}>Email</th>
                      <th style={{ padding: '0.5rem 0.4rem' }}>Phone</th>
                      <th style={{ padding: '0.5rem 0.4rem' }}>Postcode</th>
                      <th style={{ padding: '0.5rem 0.4rem' }}>DOB</th>
                      <th style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>Cases</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.members.map((m: ClusterMember) => {
                      const checked = selectedIds.has(m.ContactID);
                      const isMaster = masterId === m.ContactID;
                      return (
                        <tr
                          key={m.ContactID}
                          style={{
                            background: isMaster ? 'var(--bg-secondary)' : 'transparent',
                            opacity: checked ? 1 : 0.45,
                            borderBottom: '1px solid var(--border-color)',
                          }}
                        >
                          <td style={{ padding: '0.45rem 0.4rem' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSelect(m.ContactID)}
                            />
                          </td>
                          <td style={{ padding: '0.45rem 0.4rem' }}>
                            <input
                              type="radio"
                              name="master"
                              checked={isMaster}
                              disabled={!checked}
                              onChange={() => setMasterId(m.ContactID)}
                              title="Set as master"
                            />
                          </td>
                          <td
                            style={{
                              padding: '0.45rem 0.4rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <span
                              style={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationColor: 'var(--border-color)',
                              }}
                              title="Open contact in new tab"
                              onClick={() => window.open(`/contacts/${m.ContactID}`, '_blank')}
                            >
                              {m['First Name']} {m['Last Name']}
                            </span>
                            {isMaster && (
                              <span
                                style={{
                                  fontSize: '0.68rem',
                                  marginLeft: '0.4rem',
                                  color: 'var(--accent-primary)',
                                  fontWeight: 700,
                                }}
                              >
                                MASTER
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              padding: '0.45rem 0.4rem',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {m.ContactID}
                          </td>
                          <td
                            style={{
                              padding: '0.45rem 0.4rem',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {m.Email || '—'}
                          </td>
                          <td style={{ padding: '0.45rem 0.4rem', whiteSpace: 'nowrap' }}>
                            {m['Phone (main)'] || '—'}
                          </td>
                          <td style={{ padding: '0.45rem 0.4rem' }}>{m.Postcode || '—'}</td>
                          <td style={{ padding: '0.45rem 0.4rem', whiteSpace: 'nowrap' }}>
                            {m.DateOfBirth ? m.DateOfBirth.slice(0, 10) : '—'}
                          </td>
                          <td
                            style={{
                              padding: '0.45rem 0.4rem',
                              textAlign: 'right',
                              fontWeight: 500,
                            }}
                          >
                            {m.case_count ?? 0}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  fontSize: '0.8rem',
                  opacity: 0.6,
                  textAlign: 'right',
                  marginTop: '0.3rem',
                }}
              >
                {selectedIds.size} of {detail.members.length} selected
              </div>
            </>
          ) : (
            <p style={{ opacity: 0.6 }}>No members found.</p>
          )}

          {mergeCount > 0 && (
            <p
              style={{
                fontSize: '0.8rem',
                color: 'var(--error-500)',
                fontWeight: 600,
                margin: '0.75rem 0 0',
              }}
            >
              {mergeCount} contact{mergeCount > 1 ? 's' : ''} will be permanently merged into{' '}
              {masterId}.
              {isPartial &&
                ` ${(detail?.members.length ?? 0) - selectedIds.size} contact${(detail?.members.length ?? 0) - selectedIds.size > 1 ? 's' : ''} will remain in the cluster.`}{' '}
              Full snapshots are saved for audit.
            </p>
          )}
        </div>

        <div className={css.modalFooter}>
          <button className={css.btnSecondary} onClick={onClose} disabled={isPending}>
            Cancel
          </button>
          <button
            className={css.btnPrimary}
            onClick={() => masterId && onMerge(masterId, [...selectedIds])}
            disabled={isPending || !masterId || selectedIds.size < 2}
          >
            {isPending ? 'Merging...' : `Merge ${mergeCount} Into ${masterId ?? '...'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
 *  Missing Data Panel
 * ═══════════════════════════════════════════ */
function MissingDataPanel() {
  const { data: stats, isLoading } = useMissingDataStats();
  const [drillField, setDrillField] = useState<MissingField | null>(null);
  const [drillPage, setDrillPage] = useState(0);
  const drillPageSize = 30;

  const { data: drillData } = useMissingDataContacts(
    drillField || 'email',
    drillPageSize,
    drillPage * drillPageSize,
    !!drillField,
  );

  if (isLoading) return <p style={{ marginTop: '1rem' }}>Loading...</p>;
  if (!stats) return <p style={{ marginTop: '1rem', opacity: 0.6 }}>No data available.</p>;

  const totalContacts = parseInt(String(stats.total_contacts), 10);

  return (
    <div style={{ marginTop: '1rem' }}>
      <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '1rem' }}>
        Overview of contacts with missing information. Total contacts:{' '}
        <strong>{totalContacts.toLocaleString()}</strong>
      </p>

      {/* Stat Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: '0.8rem',
          marginBottom: '1.5rem',
        }}
      >
        {MISSING_FIELDS.map(({ key, label, statKey }) => {
          const count = parseInt(
            String((stats as unknown as Record<string, unknown>)[statKey] ?? 0),
            10,
          );
          const pct = totalContacts > 0 ? ((count / totalContacts) * 100).toFixed(1) : '0.0';
          const isHigh = count / totalContacts > 0.2;
          return (
            <button
              key={key}
              onClick={() => {
                setDrillField(key);
                setDrillPage(0);
              }}
              style={{
                padding: '0.8rem',
                borderRadius: '10px',
                textAlign: 'left',
                cursor: 'pointer',
                border:
                  drillField === key
                    ? '2px solid var(--accent-primary)'
                    : '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                transition: 'border 0.15s',
              }}
            >
              <div style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '0.3rem' }}>
                {label}
              </div>
              <div
                style={{
                  fontSize: '1.3rem',
                  fontWeight: 700,
                  color: isHigh ? 'var(--error-500)' : 'var(--text-primary)',
                }}
              >
                {count.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.7rem', opacity: 0.5 }}>{pct}% of contacts</div>
            </button>
          );
        })}
      </div>

      {/* Drill-down Table */}
      {drillField && drillData && (
        <>
          <h4 style={{ marginBottom: '0.4rem' }}>
            {MISSING_FIELDS.find((f) => f.key === drillField)?.label} — Missing (
            {drillData.total.toLocaleString()} contacts)
          </h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                <th style={{ padding: '0.4rem 0.6rem' }}>Contact ID</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>First Name</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>Last Name</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>Email</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>Phone</th>
                <th style={{ padding: '0.4rem 0.6rem' }}>Postcode</th>
              </tr>
            </thead>
            <tbody>
              {drillData.contacts.map((c: MissingDataContact) => (
                <tr
                  key={c.ContactID}
                  onClick={() => window.open(`/contacts/${c.ContactID}`, '_blank')}
                  style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.04))')
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                  title="Open contact"
                >
                  <td
                    style={{
                      padding: '0.4rem 0.6rem',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                    }}
                  >
                    {c.ContactID}
                  </td>
                  <td style={{ padding: '0.4rem 0.6rem' }}>
                    {c['First Name'] || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                  <td style={{ padding: '0.4rem 0.6rem' }}>
                    {c['Last Name'] || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                  <td style={{ padding: '0.4rem 0.6rem' }}>
                    {c.Email || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                  <td style={{ padding: '0.4rem 0.6rem' }}>
                    {c['Phone (main)'] || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                  <td style={{ padding: '0.4rem 0.6rem' }}>
                    {c.Postcode || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {Math.ceil(drillData.total / drillPageSize) > 1 && (
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'center',
                marginTop: '0.75rem',
              }}
            >
              <button
                className={css.btnSmall}
                disabled={drillPage === 0}
                onClick={() => setDrillPage((p) => p - 1)}
              >
                Prev
              </button>
              <span style={{ padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}>
                Page {drillPage + 1} of {Math.ceil(drillData.total / drillPageSize)}
              </span>
              <button
                className={css.btnSmall}
                disabled={drillPage >= Math.ceil(drillData.total / drillPageSize) - 1}
                onClick={() => setDrillPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
 *  Merge History Panel
 * ═══════════════════════════════════════════ */
function MergeHistoryPanel() {
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const { data, isLoading } = useMergeHistory(pageSize, page * pageSize);

  if (isLoading) return <p style={{ marginTop: '1rem' }}>Loading...</p>;
  if (!data?.merges.length)
    return <p style={{ marginTop: '1rem', opacity: 0.6 }}>No merges performed yet.</p>;

  const totalPages = Math.ceil(data.total / pageSize);

  return (
    <div style={{ marginTop: '1rem' }}>
      <p style={{ margin: '0 0 0.75rem', fontWeight: 600, fontSize: '0.9rem' }}>
        Total contacts merged: <span style={{ fontWeight: 700 }}>{data.total}</span>
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
            <th style={{ padding: '0.4rem 0.6rem' }}>Date</th>
            <th style={{ padding: '0.4rem 0.6rem' }}>Master</th>
            <th style={{ padding: '0.4rem 0.6rem' }}>Merged (removed)</th>
            <th style={{ padding: '0.4rem 0.6rem' }}>Cases Moved</th>
            <th style={{ padding: '0.4rem 0.6rem' }}>Fields Filled</th>
            <th style={{ padding: '0.4rem 0.6rem' }}>By</th>
          </tr>
        </thead>
        <tbody>
          {data.merges.map((m: ContactMergeLog) => (
            <tr key={m.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
              <td style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}>
                {new Date(m.performed_at).toLocaleDateString()}{' '}
                {new Date(m.performed_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </td>
              <td style={{ padding: '0.4rem 0.6rem', fontFamily: 'monospace' }}>
                {m.master_contact_id}
              </td>
              <td style={{ padding: '0.4rem 0.6rem', fontFamily: 'monospace', opacity: 0.6 }}>
                {m.merged_contact_id}
              </td>
              <td style={{ padding: '0.4rem 0.6rem' }}>{m.cases_reassigned}</td>
              <td style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem' }}>
                {m.fields_merged ? Object.keys(m.fields_merged).join(', ') || 'None' : 'None'}
              </td>
              <td style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}>{m.performed_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div
          style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '0.75rem' }}
        >
          <button
            className={css.btnSmall}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>
          <span style={{ padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className={css.btnSmall}
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
 *  Case Duplicates Panel
 * ═══════════════════════════════════════════ */
function CaseDuplicatesPanel() {
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { showToast } = useToast();
  const { data: summary, refetch: refetchSummary } = useCaseDuplicateGroupSummary();
  const {
    data: groupsData,
    isLoading,
    refetch: refetchGroups,
  } = useCaseDuplicateGroups('open', pageSize, page * pageSize);
  const scanMutation = useRunCaseDuplicateScan();

  // Async scan polling
  const [scanning, setScanning] = useState(false);
  const { data: scanStatus } = useCaseScanStatus(scanning);
  const prevStatusRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = scanStatus?.status;
    prevStatusRef.current = curr;
    if (!curr || curr === prev) return;

    if (curr === 'complete' && prev === 'running') {
      setScanning(false);
      const r = scanStatus?.result;
      showToast(
        `Case scan complete: ${r?.inserted ?? 0} new groups, ${r?.updated ?? 0} updated, ${r?.pruned ?? 0} pruned.`,
        'success',
        6000,
      );
      refetchSummary();
      refetchGroups();
    } else if (curr === 'error' && prev === 'running') {
      setScanning(false);
      showToast(`Case scan failed: ${scanStatus?.error ?? 'unknown error'}`, 'error', 6000);
    }
  }, [scanStatus?.status]);

  const [expandedGroup, setExpandedGroup] = useState<number | null>(null);
  const [dismissTarget, setDismissTarget] = useState<number | null>(null);
  const [dismissNote, setDismissNote] = useState('');
  const dismissMutation = useDismissCaseDuplicateGroup();

  const handleScan = () => {
    scanMutation.mutate(undefined, {
      onSuccess: () => {
        setScanning(true);
        showToast('Case scan started...', 'info', 5000);
      },
      onError: (err) => showToast(`Could not start scan: ${(err as Error).message}`, 'error'),
    });
  };

  const handleDismiss = (id: number) => {
    dismissMutation.mutate(
      { id, note: dismissNote },
      {
        onSuccess: () => {
          setDismissTarget(null);
          setDismissNote('');
          showToast('Group dismissed.', 'success');
        },
      },
    );
  };

  const isScanning = scanMutation.isPending || scanning;
  const totalPages = groupsData ? Math.ceil(groupsData.total / pageSize) : 0;

  return (
    <div style={{ marginTop: '1rem' }}>
      {/* Scan button */}
      <div
        style={{
          display: 'flex',
          gap: '1.5rem',
          alignItems: 'center',
          marginBottom: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <button onClick={handleScan} disabled={isScanning} className={css.btnPrimary}>
          {isScanning ? 'Scanning...' : 'Scan for Case Duplicates'}
        </button>
        {isScanning && (
          <div style={{ flex: 1, minWidth: '200px', maxWidth: '300px' }}>
            <div
              style={{
                height: '6px',
                borderRadius: '3px',
                background: 'var(--bg-secondary)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  borderRadius: '3px',
                  background: 'var(--accent-primary)',
                  animation: 'indeterminate 1.5s infinite ease-in-out',
                  width: '40%',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div
          style={{
            display: 'flex',
            gap: '1.5rem',
            alignItems: 'center',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            fontSize: '0.85rem',
          }}
        >
          <span>
            Open groups: <strong>{summary.open}</strong>
          </span>
          <span>
            Dismissed: <strong>{summary.dismissed}</strong>
          </span>
          <span>
            Resolved: <strong>{summary.resolved}</strong>
          </span>
          <span>
            Total duplicate cases: <strong>{summary.total_duplicate_cases}</strong>
          </span>
        </div>
      )}

      {/* Group list */}
      {isLoading ? (
        <p>Loading...</p>
      ) : !groupsData?.groups.length ? (
        <p style={{ opacity: 0.6 }}>No open case duplicate groups. Run a scan to check.</p>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {groupsData.groups.map((g: CaseDuplicateGroup) => (
              <CaseDuplicateGroupRow
                key={g.id}
                group={g}
                isExpanded={expandedGroup === g.id}
                onToggle={() => setExpandedGroup(expandedGroup === g.id ? null : g.id)}
                onDismiss={() => setDismissTarget(g.id)}
                dismissTarget={dismissTarget}
                dismissNote={dismissNote}
                setDismissNote={setDismissNote}
                onConfirmDismiss={() => handleDismiss(g.id)}
                onCancelDismiss={() => setDismissTarget(null)}
                isDismissing={dismissMutation.isPending}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'center',
                marginTop: '0.75rem',
              }}
            >
              <button
                className={css.btnSmall}
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </button>
              <span style={{ padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                className={css.btnSmall}
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Case Duplicate Group Row ── */
function CaseDuplicateGroupRow({
  group,
  isExpanded,
  onToggle,
  onDismiss,
  dismissTarget,
  dismissNote,
  setDismissNote,
  onConfirmDismiss,
  onCancelDismiss,
  isDismissing,
}: {
  group: CaseDuplicateGroup;
  isExpanded: boolean;
  onToggle: () => void;
  onDismiss: () => void;
  dismissTarget: number | null;
  dismissNote: string;
  setDismissNote: (v: string) => void;
  onConfirmDismiss: () => void;
  onCancelDismiss: () => void;
  isDismissing: boolean;
}) {
  const { data: detail } = useCaseDuplicateGroupDetails(isExpanded ? group.id : null);

  const contactName =
    [group.contact_first_name, group.contact_last_name].filter(Boolean).join(' ') ||
    `Contact ${group.contact_id}`;

  return (
    <div
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        overflow: 'hidden',
        background: isExpanded ? 'var(--bg-secondary)' : 'transparent',
      }}
    >
      {/* Header */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          padding: '0.6rem 0.8rem',
          cursor: 'pointer',
          fontSize: '0.85rem',
        }}
      >
        <span style={{ fontFamily: 'monospace', width: '1.2rem', textAlign: 'center' }}>
          {isExpanded ? '▾' : '▸'}
        </span>
        <span
          style={{
            padding: '2px 10px',
            borderRadius: '12px',
            fontWeight: 700,
            fontSize: '0.8rem',
            background:
              group.case_count > 5
                ? 'var(--error-50)'
                : group.case_count > 2
                  ? 'var(--warning-50)'
                  : 'var(--surface-secondary)',
            color: 'var(--text-primary)',
            minWidth: '2rem',
            textAlign: 'center',
          }}
        >
          {group.case_count}
        </span>
        <span style={{ fontWeight: 600 }}>{contactName}</span>
        <span style={{ fontSize: '0.78rem', opacity: 0.6 }}>
          {group.case_type_name || `Type #${group.case_type_id}`}
        </span>
        {group.defendant_name && (
          <span style={{ fontSize: '0.78rem', opacity: 0.6 }}>vs {group.defendant_name}</span>
        )}
        <div
          style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}
          onClick={(e) => e.stopPropagation()}
        >
          {dismissTarget === group.id ? (
            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
              <input
                placeholder="Note (optional)"
                value={dismissNote}
                onChange={(e) => setDismissNote(e.target.value)}
                style={{
                  width: '120px',
                  padding: '2px 6px',
                  fontSize: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                }}
              />
              <button
                className={css.btnSmall}
                style={{ fontSize: '0.7rem' }}
                onClick={onConfirmDismiss}
                disabled={isDismissing}
              >
                OK
              </button>
              <button
                className={css.btnSmall}
                style={{ fontSize: '0.7rem' }}
                onClick={onCancelDismiss}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className={css.btnSmall}
              style={{ fontSize: '0.75rem', opacity: 0.7 }}
              onClick={onDismiss}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {/* Expanded detail — case table */}
      {isExpanded && detail && (
        <div style={{ padding: '0 0.8rem 0.8rem', borderTop: '1px solid var(--border-color)' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.82rem',
              marginTop: '0.5rem',
            }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                <th style={{ padding: '0.3rem 0.5rem' }}>CaseRef</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Created</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Stage</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Assigned To</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Proclaim Ref</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Closure</th>
              </tr>
            </thead>
            <tbody>
              {detail.cases.map((c: CaseDuplicateGroupCase) => (
                <tr
                  key={c.CaseRef}
                  onClick={() => window.open(`/cases/${c.CaseRef}`, '_blank')}
                  style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                  title="Open case in new tab"
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.04))')
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  <td
                    style={{
                      padding: '0.3rem 0.5rem',
                      fontFamily: 'monospace',
                      fontSize: '0.78rem',
                    }}
                  >
                    {c.CaseRef}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                    {c.create_date ? (
                      new Date(c.create_date).toLocaleDateString()
                    ) : (
                      <span style={{ opacity: 0.3 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    {c.stage_name || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                    {c.assigned_user_id || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                  <td
                    style={{
                      padding: '0.3rem 0.5rem',
                      fontFamily: 'monospace',
                      fontSize: '0.78rem',
                    }}
                  >
                    {c.proclaim_ref || <span style={{ opacity: 0.3 }}>—</span>}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem', fontSize: '0.78rem' }}>
                    {c.closure_date ? (
                      <span>
                        {new Date(c.closure_date).toLocaleDateString()}{' '}
                        {c.closure_reason && `(${c.closure_reason})`}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>Open</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
